import React from "react";
import Head from "next/head";
import Link from "next/link";

import getModelMinLeasingRate from "utils/getModelMinLeasingRate";
import buildCanonicalTagHref from "utils/seo/buildCanonicalTagHref";

import config from "../../../../config";

export function BMWHead({ brandPageUrl, models }) {
  const minLeasingRate = getModelMinLeasingRate(models, "1er-Reihe");
  return (
    <Head>
      <meta
        name="description"
        content={`BMW Neuwagen, Jahreswagen, Gebrauchtwagen ✓ Privat- + Gewerbeleasing ✓ Z.B. BMW 1er schon ab ${minLeasingRate} ➔ Holen Sie sich jetzt Ihr Schnäppchen`}
      />
      <meta
        name="robots"
        content={config.robotsContent}
      />
      <link rel="canonical" href={buildCanonicalTagHref(brandPageUrl || "")} />
      <title>Günstige BMW Leasing Angebote ohne Anzahlung</title>
    </Head>
  );
}

const toc = [
  {
    topic: "Wie funktioniert Leasing bei BMW?",
    anchor: "wie-funktioniert-leasing-bei-bmw",
  },
  {
    topic: "Was beinhaltet BMW Leasing?",
    anchor: "was-beinhaltet-bmw-leasing",
  },
  {
    topic: "Was kostet Leasing bei BMW?",
    anchor: "was-kostet-leasing-bei-bmw",
  },
  {
    topic: "BMW Neu- oder Gebrauchtwagen Leasing?",
    anchor: "bmw-neu-oder-gebrauchtwagen-leasing",
  },
  {
    topic: "BMW Privatleasing vs. Gewerbeleasing",
    anchor: "bmw-privatleasing-vs-gewerbeleasing",
  },
  {
    topic: "Inspektion und Service bei BMW",
    anchor: "inspektion-und-service-bei-bmw",
  },
  {
    topic: "Welche sind die beliebtesten BMW Leasing Modelle?",
    anchor: "welche-sind-die-beliebtesten-bmw-leasing-modelle",
  },
  { topic: "Fazit zum BMW Leasing", anchor: "fazit-zum-bmw-leasing" },
];

export default function() {
  return (
    <div className="page-container page-container--tight px-5 mb-1">
      <h2 className="headline--4 mb-4">
        BMW Leasing – Verbinden Sie Eleganz mit Pferdestärke
      </h2>
      <p className="mb-4">
        BMW Modelle verbinden motorisierte Sportlichkeit mit Eleganz und höchstem
        Komfort. Sie finden darunter sportliche Coupés, die edle Business
        Limousine, wie auch den luxuriösen Familien Allrounder. Und wenn Sie
        möchten sogar alles in einem.
      </p>
      <p className="mb-4">
        <b>In unserem BMW Leasing Vergleich bieten wir Ihnen:</b>
      </p>
      <ul className="mb-4">
        <li>
          über
          {" "}
          <b>20 erstklassige BMW Leasingmodelle</b>
        </li>
        <li>
          besonders
          {" "}
          <b>günstige Leasingraten</b>
        </li>
        <li>
          Ihr individuell passendes Angebot für ein BMW Leasing
          {" "}
          <b>ohne Anzahlung</b>
        </li>
        <li>
          und selbstverständlich
          {" "}
          <b>alle Fahrzeugtypen</b>
        </li>
      </ul>
      <p className="mb-4">
        Außerdem stellen wir Ihnen Autos von über 100 autorisierten und seriösen
        Händlern aus ganz Deutschland vor und bieten Ihnen somit eine
        herausragende Auswahl an Leasingfahrzeugen. Den Leasingvertrag schließen
        Sie bei uns direkt mit der BMW Bank GmbH ab.
      </p>
      <p className="mb-5 lg:mb-8">
        Die BMW Leasing Angebote bieten ein besonders hohes Maß an Flexibilität in
        ihren Leasingmöglichkeiten. Zum Beispiel legen Sie die spezifischen
        Einzelheiten, wie die Höhe der monatlichen Raten, Vertragslaufzeit und
        Kilometerleistung ganz nach Ihren Budget Überlegungen fest.
      </p>
      <ol className="mb-5 lg:mb-8">
        {toc.map(({ topic, anchor, subTopics }) => {
          if (subTopics && subTopics.length > 0) {
            return (
              <li key={anchor} className="text-secondary font-bold">
                {topic}
                <ul style={{ paddingLeft: "20px", listStyle: "disc" }}>
                  {subTopics.map(({ topic, anchor }) => (
                    <li key={anchor} className="text-secondary font-bold">
                      <Link href={`#${anchor}`} className="link link--secondary">
                        {topic}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            );
          }
          return (
            <li key={anchor} className="text-secondary font-bold">
              <Link href={`#${anchor}`} className="link link--secondary">
                {topic}
              </Link>
            </li>
          );
        })}
      </ol>

      {/* Wie funktioniert Leasing bei BMW? */}
      <div id="wie-funktioniert-leasing-bei-bmw" className="pt-2 mb-10 lg:mb-12">
        <h3 className="headline--6 mb-5 lg:mb-6">
          Wie funktioniert Leasing bei BMW?
        </h3>
        <p className="mb-4">
          Entscheiden Sie ganz
          {" "}
          <b>nach Ihren Bedürfnissen zwischen drei</b>
          {" "}
          Leasingmöglichkeiten:
        </p>
        <div className="flex flex-col md:flex-row -mx-1 mb-1">
          <div className="flex flex-col px-1 w-full md:w-1/3 mb-4">
            <div className="p-2 flex flex-col flex-grow rounded border border-grey">
              <header className="-mx-2 px-3 h-15 border-b border-grey">
                <p className="font-bold">BMW Komfortleasing</p>
              </header>
              <ul className="py-3 pl-5 pr-1">
                <li>Finanzieller Spielraum</li>
                <li>
                  Flexible monatliche Raten durch Anpassung der Vertragslaufzeit,
                  der jährlichen Kilometerleistung und optionalen
                  Leasing-Sonderzahlung
                </li>
                <li>
                  Nach Vertragsende, unkomplizierter Umstieg auf ein anderes BMW
                  Modell
                </li>
              </ul>
            </div>
          </div>
          <div className="flex flex-col px-1 w-full md:w-1/3 mb-4">
            <div className="p-2 flex flex-col flex-grow rounded border border-grey">
              <header className="-mx-2 px-3 h-15 border-b border-grey">
                <p className="font-bold">
                  BMW i Leasing
                  {" "}
                  <br />
                  (Elektrofahrzeuge)
                </p>
              </header>
              <ul className="py-3 pl-5 pr-1">
                <li>Basis: BMW Komfortleasing</li>
                <li>Nachhaltig und innovativ</li>
                <li>
                  Optional: Plus Leasing
                  <ul className="pl-5 pr-1">
                    <li>KFZ- Vollkasko und Haftpflicht</li>
                    <li>
                      Monatliche Versicherungsprämie unabhängig vom persönlichen
                      Schadenfreiheitsrabatt
                    </li>
                    <li>
                      Keine Auf- oder Abschläge, da kein Einfluss der
                      Regionalklassen besteht
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
          <div className="flex flex-col px-1 w-full md:w-1/3 mb-4">
            <div className="p-2 flex flex-col flex-grow rounded border border-grey">
              <header className="-mx-2 px-3 h-15 border-b border-grey">
                <p className="font-bold">BMW Business Leasing</p>
              </header>
              <ul className="py-3 pl-5 pr-1">
                <li>Passen Sie Ihren Vertrag nach Ihren Wünschen an</li>
                <li>
                  Attraktive Konditionen, auf Wunsch auch mit Reifenservice,
                  Wartung und Reparatur
                </li>
                <li>Wählen Sie unter den aktuellsten BMW Modellen</li>
              </ul>
            </div>
          </div>
        </div>
        <p className="mb-4">
          Unsere BMW Modelle können Sie (fast) alle ohne Anzahlung leasen. Das
          heißt, Sie müssen lediglich einen kleinen regelmäßigen Geldbetrag in die
          Hand nehmen, um Ihr Traumauto fahren zu können.
        </p>
        <p className="mb-4">
          <b>Hinweis:</b>
          {" "}
          Voraussetzung für ein BMW Leasing ohne Anzahlung ist,
          dass Sie einen positiven Schufa Score vorweisen können.
        </p>
        <p>
          Bei uns leasen Sie Ihren BMW per Kilometerleasing. D.h. das Risiko einer
          unerfreulichen Nachzahlung am Ende der Vertragslaufzeit gibt es nicht
          und Sie können Ihren neuen BMW ganz sorgenfrei fahren.
        </p>
      </div>

      {/* Was beinhaltet BMW Leasing? */}
      <div id="was-beinhaltet-bmw-leasing" className="pt-2 mb-10 lg:mb-12">
        <h3 className="headline--6 mb-5 lg:mb-6">Was beinhaltet BMW Leasing?</h3>
        <p className="mb-4">
          BMW lässt Ihnen beim Autoleasing die Wahl. Sie können sich entscheiden
          zwischen dem neusten Modell oder einem attraktiven Gebrauchtwagen.
        </p>
        <p className="mb-4">
          BMW Leasing bietet einen besonders umfangreichen Service. Zum Beispiel
          (unter bestimmten Voraussetzungen) einen Ersatzwagen bis zu zweimal im
          Jahr. Zusätzlich gibt es viele weitere Pakete zwischen denen Sie wählen
          können.
        </p>
        <p>
          Die BMW Financial Service stellt außerdem vielfältige
          Versicherungsoptionen bereit. Sie können sich also auf das wichtigste
          konzentrieren - Ihren ausgesuchten BMW.
        </p>
      </div>

      {/* Was kostet Leasing bei BMW? */}
      <div id="was-kostet-leasing-bei-bmw" className="pt-2 mb-10 lg:mb-12">
        <h3 className="headline--6 mb-5 lg:mb-6">Was kostet Leasing bei BMW?</h3>
        <p>
          Die Preise variieren je nach Modell, Leistung, Antrieb und
          Ausstattungsmöglichkeiten. Filtern Sie in unserer Fahrzeugsuche nach
          ihren Wünschen und lassen Sie sich von unseren
          {" "}
          <Link href="/suche?marke=BMW" className="link link--secondary">
            Leasingangeboten überzeugen
          </Link>
          .
        </p>
      </div>

      {/* BMW Neu- oder Gebrauchtwagen Leasing? */}
      <div
        id="bmw-neu-oder-gebrauchtwagen-leasing"
        className="pt-2 mb-10 lg:mb-12"
      >
        <h3 className="headline--6 mb-5 lg:mb-6">
          BMW Neu- oder Gebrauchtwagen Leasing?
        </h3>
        <p className="mb-4">
          Sie haben die freie Wahl zwischen Gebrauchtwagen Leasing oder Neuwagen
          Leasing. Es ist also ganz Ihnen überlassen, ob Sie immer die neuesten
          Modelle fahren oder einen
          {" "}
          <b>sofort verfügbaren</b>
          {" "}
          Jahreswagen
          bevorzugen.
        </p>
        <p className="mb-4">
          Das BMW Gebrauchtwagen Leasing ist oft deutlich günstiger als das
          Neuwagen Leasing. Denn der hohe Wertverlust ist hier bereits angefallen.
          Zudem müssen Sie, wie schon erwähnt, nicht lange auf den für Sie
          perfekten BMW warten.
        </p>
        <p>
          Bei den meisten Gebrauchtwagenleasings besteht die Gefahr von
          aufwändigen Reparaturen durch den bereits angefallenen
          Fahrzeugverschleiß.
          {" "}
          <b>Nicht bei uns!</b>
          {" "}
          In der Regel bieten wir Ihnen
          Vorführwagen oder „junge“ Gebrauchte an, die selten mehr als 20.000
          Kilometer gefahren wurden. D.h. Sie müssen sich keine Sorgen um
          anfallende Zusatzkosten machen.
        </p>
      </div>

      {/* BMW Privatleasing vs. Gewerbeleasing */}
      <div
        id="bmw-privatleasing-vs-gewerbeleasing"
        className="pt-2 mb-10 lg:mb-12"
      >
        <h3 className="headline--6 mb-5 lg:mb-6">
          BMW Privatleasing vs. Gewerbeleasing
        </h3>
        <p className="mb-4">
          BMW bietet sowohl Geschäfts- als auch Privat Leasing an - und das immer
          als Kilometerleasing.
        </p>
        <p className="mb-4">
          Beim attraktiven Privatleasing erhalten Sie immer die neuesten Modelle
          zu einem super Preis. Die Gefahr eines hohen Wertverlustes zum Ende der
          Leasingzeit besteht hier nicht, da der Wert Ihres Fahrzeuges bereits zu
          Leasingbeginn festgelegt ist. Somit haben Sie auch keinerlei Probleme
          Ihr Auto nach Vertragslaufzeit verkaufen zu müssen. Sie geben es einfach
          wieder zurück und suchen sich ein neues Modell aus.
        </p>
        <p>
          Unternehmen können das Leasing direkt als monatliche Ausgabe absetzen
          und so, zusätzlich zu den bereits sehr günstigen Konditionen, auch noch
          steuerliche Vorteile geltend machen. Außerdem können Firmen durch das
          BMW Gewerbe Leasing immer die edelsten Modelle zu moderaten Preisen
          vorführen.
        </p>
      </div>

      {/* Inspektion und Service bei BMW */}
      <div id="inspektion-und-service-bei-bmw" className="pt-2 mb-10 lg:mb-12">
        <h3 className="headline--6 mb-5 lg:mb-6">
          Inspektion und Service bei BMW
        </h3>
        <p className="mb-4">
          BMW Leasing hat eine ganz besonders hohe Serviceleistung.
        </p>
        <p className="mb-4">
          Sie können unter verschiedenen Service Paketen wählen:
        </p>
        <ul className="mb-4">
          <li>BMW Service Paket Motoröl</li>
          <li>BME Service Paket Bremse</li>
          <li>BMW Service Paket Fahrzeugcheck u.v.m.</li>
        </ul>
        <p>
          Außerdem können wir Ihnen demnächst mit BMW Service Inclusive auch das
          Rundumpaket anbieten. Dort erhalten Sie alle Serviceleistungen für einen
          einmaligen Festbetrag.
        </p>
      </div>

      {/* Welche sind die beliebtesten BMW Leasing Modelle? */}
      <div
        id="welche-sind-die-beliebtesten-bmw-leasing-modelle"
        className="pt-2 mb-10 lg:mb-12"
      >
        <h3 className="headline--6 mb-5 lg:mb-6">
          Welche sind die beliebtesten BMW Leasing Modelle?
        </h3>
        <p className="mb-4">
          Besonders sticht der 5er BMW hervor. Er vereint das, was BMW
          auszeichnet: Fahrdynamik, Sportlichkeit und Ästhetik. Dank der fairen
          Leasing Subventionen findet man hier ein erstklassiges
          Preis-Leistungsverhältnis. Es ist das perfekte Business- und Familien
          Auto.
        </p>
        <p className="mb-4">Weitere interessante Fahrzeugmodelle bei BMW sind:</p>
        <ul className="mb-4">
          <li>3er BMW</li>
          <li>i3</li>
          <li>1er BMW</li>
        </ul>
        <p className="mb-4">
          Die nachhaltigen i Modelle werden immer beliebter und weisen derzeit
          einen deutlichen Aufschwung vor. Das ist nicht zuletzt den besonders
          hohen Steuervorteilen bei Elektroautos zu verdanken.
        </p>
        <p>
          Was wird also Ihr
          {" "}
          <Link href="#modellreihen" className="link link--secondary">
            BMW Leasing Modell
          </Link>
          ?
        </p>
      </div>

      {/* Fazit zum BMW Leasing */}
      <div id="fazit-zum-bmw-leasing" className="pt-2 mb-2">
        <h3 className="headline--6 mb-5 lg:mb-6">Fazit zum BMW Leasing</h3>
        <p className="mb-4">
          Vom wendigen 1er BMW über die edle Luxus Limousine bis hin zur sportlich
          kompakten 5er oder 3er Reihe erfüllen wir Ihnen Ihre BMW Wünsche mit
          außergewöhnlich guten Leasingangeboten. Zudem finden Sie bei uns
          regelmäßig besonders günstige Leasing Schnäppchen für Ihren BMW.
        </p>
        <p className="mb-4">
          Ganz besonders hervorzuheben sind außerdem unsere attraktiven Angebote
          des BMW Leasing ohne Anzahlung. Und das für Geschäfts- und Privatkunden.
          BMW bietet Ihnen sensationelle Service Angebote, die je nach Wunsch auf
          Sie angepasst werden können.
        </p>
        <p>
          Überzeugen Sie sich also von unseren
          {" "}
          <Link href="/suche?marke=BMW" className="link link--secondary">
            BMW Leasing Angeboten
          </Link>
          {" "}
          zu fairen Konditionen. Denn dann können auch Sie schon bald mit einem
          eleganten und starken BMW hervorstechen.
        </p>
      </div>
    </div>
  );
}
